import { useMemo, useState } from 'react';
import { adminReviewMetadata, adminRows } from './adminSchema';
import type { AdminRow } from './adminSchema';

const columns = adminReviewMetadata.properties.tableField.tableLayoutDefinition;

interface SelectOption {
  label: string;
  value: string;
}

/**
 * Schema-driven review table — adapted from webapp2's dynamic table UX.
 * Columns, widths and editability come from `adminReviewMetadata`; reviewer
 * decisions (L1/L2/L3) and comments are editable in place.
 */
export const DataReview = () => {
  const [rows, setRows] = useState<AdminRow[]>(adminRows);

  const update = (id: number, name: string, value: string) =>
    setRows((prev) =>
      prev.map((row) => (row.id === id ? ({ ...row, [name]: value } as AdminRow) : row))
    );

  const attested = useMemo(
    () => rows.filter((r) => [r.L1, r.L2, r.L3].some((v) => v === 'attest')).length,
    [rows]
  );

  return (
    <div className="dashboard">
      <div className="dashboard__head">
        <div>
          <h1>Admin Review</h1>
          <p>Attest or reject data elements across review levels. {attested} of {rows.length} have a decision.</p>
        </div>
      </div>

      <section className="panel">
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.name} style={{ minWidth: col.width }}>
                    <span className="th-sort" style={{ cursor: 'default' }}>
                      {col.label}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  {columns.map((col) => {
                    const value = String((row as unknown as Record<string, unknown>)[col.name] ?? '');
                    const editable = !!col.options?.editable;
                    const options = (col.options?.selectOptions as SelectOption[] | undefined) ?? [];

                    if (col.type === 'select' && editable) {
                      return (
                        <td key={col.name}>
                          <select
                            className="select select--sm"
                            value={value}
                            onChange={(e) => update(row.id, col.name, e.target.value)}
                          >
                            {options.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      );
                    }

                    if (col.type === 'text' && editable) {
                      return (
                        <td key={col.name}>
                          <input
                            className="cell-input"
                            value={value}
                            placeholder="Add comment…"
                            onChange={(e) => update(row.id, col.name, e.target.value)}
                          />
                        </td>
                      );
                    }

                    return <td key={col.name}>{value}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
